import { Controller } from "@hotwired/stimulus"

// Section-order editor (dashboard_layouts/edit). Each row carries a hidden
// `sections[]` input, so the DOM order IS the submitted order: the arrows and
// the drag only ever move rows, and the form posts whatever is on screen.
//
// Moves animate with FLIP: snapshot every row's top, move the DOM, then play
// each row from where it was to where it now sits. The drag translates the
// held row under the finger and slides its neighbours out of the way by one
// row pitch; the DOM is only reordered on release.
//
// Save (the native nav-bar checkmark, bridge--button on the hidden submit)
// stays disabled until the order or an on/off toggle differs from how the
// page opened. Submitting plays the exit first, then lets the form go.
//
// With a url (the budget Order list in budgets/edit) there is no form: every
// move PATCHes the on-screen order as ids[] (each row's data-row), the
// sortable controller's contract, debounced so a run of arrow taps sends one
// request.
const EXIT_MS = 300
const PERSIST_MS = 400

export default class extends Controller {
  static targets = ["list", "submit"]
  static values = { url: String }

  connect() {
    // A snapshot cached mid-exit keeps .is-leaving, whose fill-forwards
    // animation holds every card at opacity 0: reopening the screen from that
    // snapshot paints it blank.
    this.element.classList.remove("is-leaving")
    this.initial = this.#state()
    this.leaving = false
    this.#sync()
    // A Save on the page (the budget form) inside the debounce waits for the
    // pending order first, so the screen it lands on already has it.
    if (this.urlValue) {
      this.flushOnSubmit = this.#flushOnSubmit.bind(this)
      document.addEventListener("submit", this.flushOnSubmit, true)
    }
  }

  // Closing the sheet inside the debounce still sends the last order: the
  // page is going, so the request outlives it (keepalive) and nothing reloads.
  disconnect() {
    if (this.flushOnSubmit) document.removeEventListener("submit", this.flushOnSubmit, true)
    if (!this.persistTimer) return

    clearTimeout(this.persistTimer)
    this.#request({ keepalive: true })
      .then(response => { if (!response.ok) throw new Error(response.statusText) })
      .catch(error => console.error("[dashboard-layout] reorder failed", error))
  }

  moveUp(event) {
    const row = event.currentTarget.closest("[data-row]")
    const prev = row?.previousElementSibling
    if (!prev) return
    this.#flip(() => this.listTarget.insertBefore(row, prev))
  }

  moveDown(event) {
    const row = event.currentTarget.closest("[data-row]")
    const next = row?.nextElementSibling
    if (!next) return
    this.#flip(() => this.listTarget.insertBefore(next, row))
  }

  change() {
    this.#sync()
  }

  grab(event) {
    if (event.button !== undefined && event.button !== 0) return
    event.preventDefault()

    const handle = event.currentTarget
    const row = handle.closest("[data-row]")
    const rows = this.#rows()
    const from = rows.indexOf(row)
    const pitch = rows.length > 1
      ? rows[1].getBoundingClientRect().top - rows[0].getBoundingClientRect().top
      : 0
    if (!pitch) return

    const startY = event.clientY
    let to = from
    let dy = 0

    handle.setPointerCapture(event.pointerId)
    row.classList.add("is-dragging")
    rows.forEach(r => { if (r !== row) r.classList.add("is-settling") })

    const move = ev => {
      const min = -from * pitch
      const max = (rows.length - 1 - from) * pitch
      dy = Math.max(min, Math.min(max, ev.clientY - startY))
      row.style.transform = `translateY(${dy}px)`
      to = Math.max(0, Math.min(rows.length - 1, from + Math.round(dy / pitch)))

      rows.forEach((r, i) => {
        if (r === row) return
        let shift = 0
        if (from < to && i > from && i <= to) shift = -pitch
        if (from > to && i < from && i >= to) shift = pitch
        r.style.transform = shift ? `translateY(${shift}px)` : ""
      })
    }

    const release = () => {
      handle.removeEventListener("pointermove", move)
      handle.removeEventListener("pointerup", release)
      handle.removeEventListener("pointercancel", release)

      // Neighbours already sit where the reorder puts them, so dropping their
      // transforms in the same frame as the DOM move shows no jump.
      rows.forEach(r => { r.classList.remove("is-settling"); r.style.transform = "" })
      if (to !== from) {
        const target = rows[to]
        this.listTarget.insertBefore(row, to > from ? target.nextElementSibling : target)
      }

      // Land the held row from wherever the finger let go of it.
      row.style.transform = `translateY(${dy - (to - from) * pitch}px)`
      row.classList.remove("is-dragging")
      this.#settle(row)
      this.#sync()
      if (to !== from) this.#persistSoon()
    }

    handle.addEventListener("pointermove", move)
    handle.addEventListener("pointerup", release)
    handle.addEventListener("pointercancel", release)
  }

  // The native checkmark clicks the hidden submit; hold the submission until
  // the rows have left, so the pop back to the dashboard follows the exit.
  submit(event) {
    if (this.leaving) return
    event.preventDefault()
    this.leaving = true
    this.element.classList.add("is-leaving")
    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : EXIT_MS
    setTimeout(() => this.element.requestSubmit(this.submitTarget), delay)
  }

  #flip(mutate) {
    const before = new Map(this.#rows().map(r => [r, r.getBoundingClientRect().top]))
    mutate()
    this.#rows().forEach(r => {
      const delta = before.get(r) - r.getBoundingClientRect().top
      if (!delta) return
      r.classList.remove("is-settling")
      r.style.transform = `translateY(${delta}px)`
      this.#settle(r)
    })
    this.#sync()
    this.#persistSoon()
  }

  // Two frames: the first commits the inverted transform, the second lets the
  // transition run from it.
  #settle(row) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      row.classList.add("is-settling")
      row.style.transform = ""
    }))
  }

  #sync() {
    const rows = this.#rows()
    rows.forEach((row, i) => {
      // --i staggers the exit in on-screen order, not the order it opened in.
      row.style.setProperty("--i", i)
      row.querySelector("[data-up]").disabled = i === 0
      row.querySelector("[data-down]").disabled = i === rows.length - 1
    })
    if (this.hasSubmitTarget) this.submitTarget.disabled = this.#state() === this.initial
  }

  #persistSoon() {
    if (!this.urlValue) return
    clearTimeout(this.persistTimer)
    this.persistTimer = setTimeout(() => this.#persist(), PERSIST_MS)
  }

  #request({ keepalive = false } = {}) {
    this.persistTimer = null
    const body = new FormData()
    this.#rows().forEach(r => body.append("ids[]", r.dataset.row))

    return fetch(this.urlValue, {
      method: "PATCH",
      body,
      keepalive,
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        "Accept": "text/vnd.turbo-stream.html, text/html"
      }
    })
  }

  async #persist() {
    try {
      const response = await this.#request()
      if (!response.ok) throw new Error(response.statusText)
      // Bubbles to document: the budget form that owns Save lives outside the list.
      this.dispatch("reordered")
    } catch (error) {
      // A silently wrong order would follow the user to every screen that
      // lists budgets; reloading shows what the server really has.
      console.error("[dashboard-layout] reorder failed", error)
      window.location.reload()
    }
  }

  #flushOnSubmit(event) {
    if (!this.persistTimer) return

    const form = event.target
    event.preventDefault()
    clearTimeout(this.persistTimer)
    this.#persist().then(() => form.requestSubmit(event.submitter))
  }

  #rows() {
    return Array.from(this.listTarget.querySelectorAll(":scope > [data-row]"))
  }

  // Row order plus which toggles are off, as one comparable string.
  #state() {
    const off = Array.from(this.element.querySelectorAll(".dl-toggle:not(:checked), .dl-card-toggle:not(:checked)")).map(t => `-${t.value}`)
    return [...this.#rows().map(r => r.dataset.row), ...off].join(",")
  }
}
